import { listProjectFiles } from './conversationApi';

// The last Knowledge base list loaded for each project this session, by
// project_id, so opening a project again (or its chat sidebar, a separate
// mount of ProjectPanel) shows it straight away while a fresh copy loads.
const cache = new Map();
// The request still on its way for each project, if any.
const pending = new Map();

export function getCachedProjectFiles(projectId) {
  return cache.get(projectId);
}

export function hasCachedProjectFiles(projectId) {
  return cache.has(projectId);
}

/** Records a list changed in place (a delete, rename or folder move). */
export function setCachedProjectFiles(projectId, files) {
  cache.set(projectId, files);
}

/**
 * Loads a project's Knowledge base list and remembers it.
 * `firstLoad`: reuse a request already on its way (a prefetch) rather than
 * start another. Only for a first load: a reload after a change must not
 * join one, or it could get back a list from before that change.
 * @param {string} projectId
 * @param {{firstLoad?: boolean}} [options]
 * @returns {Promise<Array>}
 */
export function fetchProjectFiles(projectId, { firstLoad = false } = {}) {
  if (firstLoad && pending.has(projectId)) return pending.get(projectId);
  const request = listProjectFiles(projectId).then((files) => {
    cache.set(projectId, files);
    return files;
  });
  pending.set(projectId, request);
  const clear = () => {
    if (pending.get(projectId) === request) pending.delete(projectId);
  };
  request.then(clear, clear);
  return request;
}

/**
 * Starts loading a project's list before it's opened (hovering it in the
 * Projects menu, or clicking it), so the Knowledge base usually has it by
 * the time it renders. Does nothing if it's already remembered or on its way.
 * @param {string} projectId
 */
export function prefetchProjectFiles(projectId) {
  cancelHoverPrefetch();
  if (!projectId || cache.has(projectId) || pending.has(projectId)) return;
  fetchProjectFiles(projectId).catch(() => {});
}

// Hovering waits a moment first, so running the mouse down the Projects menu
// doesn't load every project it passes over.
const HOVER_PREFETCH_DELAY_MS = 150;
let hoverTimer = null;

/** prefetchProjectFiles once the pointer has rested on a project. */
export function prefetchProjectFilesOnHover(projectId) {
  cancelHoverPrefetch();
  hoverTimer = setTimeout(() => prefetchProjectFiles(projectId), HOVER_PREFETCH_DELAY_MS);
}

/** The pointer left before the delay was up. */
export function cancelHoverPrefetch() {
  clearTimeout(hoverTimer);
  hoverTimer = null;
}
